import type { ComponentProps } from 'react'
import { cn, DropdownMenuCheckboxItem, Switch } from '@teach/ui'
import { DisabledHint } from './DisabledHint'
import type { TranslationLauncher } from './useTranslationLauncher'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/**
 * Строка «что переводим» — пункт меню с тумблером справа, как переключатели в
 * настройках. Сам тумблер — только картинка состояния: переключает пункт меню
 * (до него доходят стрелки, пробел и Enter), иначе клик ловили бы двое. Галочку и
 * рамку меню прячем — с тумблером они читались как лишние квадраты.
 */
const Scope = ({
  label,
  locked,
  checked,
  ...item
}: { label: string; locked?: boolean } & ComponentProps<typeof DropdownMenuCheckboxItem>) => {
  const on = checked === true
  return (
    <DropdownMenuCheckboxItem
      className={cn(
        'gap-3 py-2.5 pl-3 pr-3 data-[state=checked]:bg-transparent data-[state=checked]:text-foreground data-[state=checked]:focus:bg-foreground/5 [&>span:first-child]:hidden',
        // «Текст» переводится всегда: строка не гаснет — это не запрет, а факт.
        locked ? 'data-[disabled]:cursor-default data-[disabled]:opacity-100' : null,
      )}
      onSelect={(e) => e.preventDefault()}
      checked={checked}
      disabled={locked || item.disabled}
      {...item}
    >
      <span className="min-w-0 flex-1 text-sm text-foreground">{label}</span>
      <Switch checked={on} tabIndex={-1} aria-hidden className="pointer-events-none shrink-0" />
    </DropdownMenuCheckboxItem>
  )
}

/**
 * Перевод: текст переводится всегда, поэтому выбирать его нечего (строки
 * «Текст» нет). Автор решает одно — переводить ли заодно картинки с надписями.
 */
export const TranslationScope = ({ launcher }: { launcher: TranslationLauncher }) => {
  const m = useMessages(messages)
  // Сметой неразличимы «в базе нет картинок» и «у всех уже есть версия второго языка» —
  // текст верен для обоих. Пока смета летит (`undefined`), «нечего» было бы догадкой.
  const none = launcher.quote.imagesCount === 0
  return (
    <DisabledHint text={none ? m.imagesHintNone : null} inset>
      <Scope
        label={m.withImages}
        checked={launcher.showsImages}
        // Выбирать нечего, когда картинок в плане нет.
        disabled={!launcher.quote.imagesCount}
        onCheckedChange={(v) => launcher.setTranslateImages(v === true)}
      />
    </DisabledHint>
  )
}
